import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// Updated styles to match the requested layout changes
const styles = StyleSheet.create({
  empsalarypdf: {
    fontFamily: 'Helvetica',
    fontSize: 12,
    lineHeight: 1.4,
    color: '#000000',
    padding: 40,
  },
  
  empsalarypdfSlip: {
    backgroundColor: '#ffffff',
    minHeight: '100%',
  },

  empsalarypdfSlipHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 20,
    borderBottom: '2px solid #000000',
    marginBottom: 20,
  },

  empsalarypdfCompanySection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
  },

  empsalarypdfLogo: {
    width: 80,
    height: 80,
    objectFit: 'contain',
  },

  empsalarypdfCompanyName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#000000',
  },

  empsalarypdfSlipTitle: {
    textAlign: 'right',
  },

  empsalarypdfSlipTitleText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 5,
  },

  empsalarypdfMonthYear: {
    fontSize: 12,
    color: '#666666',
  },

  empsalarypdfEmployeeSection: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: '#f8f9fa',
    border: '1px solid #cccccc',
  },

  empsalarypdfEmployeeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },

  empsalarypdfInfoItem: {
    width: '50%',
    marginBottom: 10,
    flexDirection: 'row',
  },

  empsalarypdfLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#000000',
    width: 100,
    marginRight: 10,
  },

  empsalarypdfValue: {
    fontSize: 11,
    fontWeight: 'normal',
    color: '#000000',
    flex: 1,
  },

  empsalarypdfDaysSection: {
    marginBottom: 20,
  },

  empsalarypdfDaysGrid: {
    border: '1px solid #cccccc',
    padding: 12,
    backgroundColor: '#f8f9fa',
    flexDirection: 'row',
  },

  // Updated days layout with two columns
  empsalarypdfDaysColumn: {
    flex: 1,
    paddingRight: 10,
  },

  empsalarypdfDaysItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    justifyContent: 'flex-start',
  },

  empsalarypdfDaysLabel: {
    fontSize: 11,
    color: '#000000',
    marginRight: 10,
    fontWeight: 'bold',
    width: 100,
  },

  empsalarypdfDaysValue: {
    fontSize: 11,
    color: '#000000',
    fontWeight: 'normal',
    flex: 1,
  },

  empsalarypdfEarningsDeductions: {
    flexDirection: 'row',
    marginBottom: 20,
    gap: 20,
  },

  empsalarypdfEarningsSection: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    border: '1px solid #cccccc',
  },

  empsalarypdfDeductionsSection: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    border: '1px solid #cccccc',
  },

  empsalarypdfSectionHeaderED: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f0f0f0',
    padding: 8,
    borderBottom: '1px solid #cccccc',
  },

  empsalarypdfSectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#000000',
    textAlign: 'center',
  },

  empsalarypdfItemsList: {
    padding: 10,
  },

  empsalarypdfItemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    paddingBottom: 8,
    borderBottom: '1px solid #f3f4f6',
  },

  empsalarypdfItemName: {
    fontSize: 11,
    color: '#000000',
    flex: 1,
  },

  empsalarypdfItemAmount: {
    fontSize: 11,
    color: '#000000',
    textAlign: 'right',
    width: 80,
  },

  empsalarypdfTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
    paddingTop: 8,
    borderTop: '1px solid #cccccc',
  },

  empsalarypdfTotalLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#000000',
  },

  empsalarypdfTotalAmount: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#000000',
  },

  empsalarypdfNetpaySection: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: '#f8f9fa',
  },

  empsalarypdfNetpayRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },

  empsalarypdfNetpayLabel: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#000000',
  },

  empsalarypdfNetpayAmount: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#000000',
  },

  empsalarypdfNetpayWords: {
    fontSize: 10,
    color: '#666666',
    fontStyle: 'italic',
    textAlign: 'right',
  },

  empsalarypdfFooter: {
    textAlign: 'center',
    marginTop: 20,
  },

  empsalarypdfFooterText: {
    fontSize: 10,
    color: '#666666',
    fontStyle: 'italic',
  },
});

const EmpSalarypdf = ({ employee, company, paySlip }) => {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Fallback data to prevent null errors
  const safeEmployee = employee || {};
  const safeCompany = company || {};
  const safePaySlip = paySlip || {};

  const calculateTotal = (items) => {
    if (!items || typeof items !== 'object') return 0;
    return Object.values(items).reduce((sum, value) => sum + (parseFloat(value) || 0), 0);
  };

  const calculateNetPay = () => {
    const totalEarnings = calculateTotal(safePaySlip.earnings);
    const totalDeductions = calculateTotal(safePaySlip.deductions);
    return totalEarnings - totalDeductions;
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convert = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
      if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
      return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
    };

    if (num === 0) return 'Zero Rupees Only';
    return convert(Math.floor(Math.abs(num))) + ' Rupees Only';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  const formatAmount = (amount) => {
    return `Rs.${formatCurrency(amount)}`;
  };

  // Calculate net pay once to avoid repeated calculations
  const netPayAmount = calculateNetPay();

  return (
    <Document>
      <Page size="A4" style={styles.empsalarypdf}>
        <View style={styles.empsalarypdfSlip}>
          {/* Header Section */}
          <View style={styles.empsalarypdfSlipHeader}>
            <View style={styles.empsalarypdfCompanySection}>
              {safeCompany.logo_url && (
                <Image 
                  src={safeCompany.logo_url} 
                  style={styles.empsalarypdfLogo}
                />
              )}
              <Text style={styles.empsalarypdfCompanyName}>
                {safeCompany.company_name || 'Your Company Name'}
              </Text>
            </View>
            <View style={styles.empsalarypdfSlipTitle}>
              <Text style={styles.empsalarypdfSlipTitleText}>PAY SLIP</Text>
              <Text style={styles.empsalarypdfMonthYear}>
                {monthNames[(safePaySlip.month || 9) - 1]} {safePaySlip.year || 2025}
              </Text>
            </View>
          </View>

          {/* Employee Information */}
          <View style={styles.empsalarypdfEmployeeSection}>
            <View style={styles.empsalarypdfEmployeeGrid}>
              <View style={styles.empsalarypdfInfoItem}>
                <Text style={styles.empsalarypdfLabel}>Employee Name:</Text>
                <Text style={styles.empsalarypdfValue}>{safeEmployee.name}</Text>
              </View>
              <View style={styles.empsalarypdfInfoItem}>
                <Text style={styles.empsalarypdfLabel}>Employee ID:</Text>
                <Text style={styles.empsalarypdfValue}>{safeEmployee.emp_id }</Text>
              </View>
              <View style={styles.empsalarypdfInfoItem}>
                <Text style={styles.empsalarypdfLabel}>Designation:</Text>
                <Text style={styles.empsalarypdfValue}>{safeEmployee.emp_role }</Text>
              </View>
              <View style={styles.empsalarypdfInfoItem}>
                <Text style={styles.empsalarypdfLabel}>Date of Joining:</Text>
                <Text style={styles.empsalarypdfValue}>{formatDate(safeEmployee.joining_date) }</Text>
              </View>
              <View style={styles.empsalarypdfInfoItem}>
                <Text style={styles.empsalarypdfLabel}>Bank Name:</Text>
                <Text style={styles.empsalarypdfValue}>{safeEmployee.emp_bank_name }</Text>
              </View>
              <View style={styles.empsalarypdfInfoItem}>
                <Text style={styles.empsalarypdfLabel}>Account No.:</Text>
                <Text style={styles.empsalarypdfValue}>{safeEmployee.account_no }</Text>
              </View>
              <View style={styles.empsalarypdfInfoItem}>
                <Text style={styles.empsalarypdfLabel}>IFSC Code:</Text>
                <Text style={styles.empsalarypdfValue}>{safeEmployee.ifsc_code }</Text>
              </View>
              <View style={styles.empsalarypdfInfoItem}>
                <Text style={styles.empsalarypdfLabel}>PAN Number:</Text>
                <Text style={styles.empsalarypdfValue}>{safeEmployee.pan_card }</Text>
              </View>
            </View>
          </View>

          {/* Days Information - Updated to two-column layout */}
          <View style={styles.empsalarypdfDaysSection}>
            <View style={styles.empsalarypdfDaysGrid}>
              {/* First Column */}
              <View style={styles.empsalarypdfDaysColumn}>
                <View style={styles.empsalarypdfDaysItem}>
                  <Text style={styles.empsalarypdfDaysLabel}>STD days:</Text>
                  <Text style={styles.empsalarypdfDaysValue}>{safePaySlip.std_days}</Text>
                </View>
                <View style={styles.empsalarypdfDaysItem}>
                  <Text style={styles.empsalarypdfDaysLabel}>Leaves:</Text>
                  <Text style={styles.empsalarypdfDaysValue}>{safePaySlip.no_of_leaves}</Text>
                </View>
                <View style={styles.empsalarypdfDaysItem}>
                  <Text style={styles.empsalarypdfDaysLabel}>Transaction Date:</Text>
                  <Text style={styles.empsalarypdfDaysValue}>
                    {formatDate(safePaySlip.transaction_date)}
                  </Text>
                </View>
              </View>
              
              {/* Second Column */}
              <View style={styles.empsalarypdfDaysColumn}>
                <View style={styles.empsalarypdfDaysItem}>
                  <Text style={styles.empsalarypdfDaysLabel}>Paid Days:</Text>
                  <Text style={styles.empsalarypdfDaysValue}>{safePaySlip.working_days}</Text>
                </View>
                <View style={styles.empsalarypdfDaysItem}>
                  <Text style={styles.empsalarypdfDaysLabel}>LOP Days:</Text>
                  <Text style={styles.empsalarypdfDaysValue}>0</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Earnings and Deductions */}
          <View style={styles.empsalarypdfEarningsDeductions}>
            {/* Earnings */}
            <View style={styles.empsalarypdfEarningsSection}>
              <View style={styles.empsalarypdfSectionHeaderED}>
                <Text style={styles.empsalarypdfSectionTitle}>Earnings</Text>
              </View>
              <View style={styles.empsalarypdfItemsList}>
                {Object.entries(safePaySlip.earnings || {}).map(([key, value]) => (
                  <View key={key} style={styles.empsalarypdfItemRow}>
                    <Text style={styles.empsalarypdfItemName}>{key}</Text>
                    <Text style={styles.empsalarypdfItemAmount}>
                      {formatAmount(value)}
                    </Text>
                  </View>
                ))}
                <View style={styles.empsalarypdfTotalRow}>
                  <Text style={styles.empsalarypdfTotalLabel}>Total Earnings</Text>
                  <Text style={styles.empsalarypdfTotalAmount}>
                    {formatAmount(calculateTotal(safePaySlip.earnings))}
                  </Text>
                </View>
              </View>
            </View>

            {/* Deductions */}
            <View style={styles.empsalarypdfDeductionsSection}>
              <View style={styles.empsalarypdfSectionHeaderED}>
                <Text style={styles.empsalarypdfSectionTitle}>Deductions</Text>
              </View>
              <View style={styles.empsalarypdfItemsList}>
                {Object.entries(safePaySlip.deductions || {}).map(([key, value]) => (
                  <View key={key} style={styles.empsalarypdfItemRow}>
                    <Text style={styles.empsalarypdfItemName}>{key}</Text>
                    <Text style={styles.empsalarypdfItemAmount}>
                      {formatAmount(value)}
                    </Text>
                  </View>
                ))}
                <View style={styles.empsalarypdfTotalRow}>
                  <Text style={styles.empsalarypdfTotalLabel}>Total Deductions</Text>
                  <Text style={styles.empsalarypdfTotalAmount}>
                    {formatAmount(calculateTotal(safePaySlip.deductions))}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Net Pay Section */}
          <View style={styles.empsalarypdfNetpaySection}>
            <View style={styles.empsalarypdfNetpayRow}>
              <Text style={styles.empsalarypdfNetpayLabel}>Net Pay:</Text>
              <Text style={styles.empsalarypdfNetpayAmount}>
                {formatAmount(netPayAmount)}
              </Text>
            </View>
            <View style={styles.empsalarypdfNetpayWords}>
              <Text>({numberToWords(netPayAmount)})</Text>
            </View>
          </View>

          {/* Footer Note */}
          <View style={styles.empsalarypdfFooter}>
            <Text style={styles.empsalarypdfFooterText}>
              This is a computer-generated salary slip and does not require a signature.
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default EmpSalarypdf;